import { ErrorHandler, Injectable } from "@angular/core";
import { Auth, user } from "@angular/fire/auth";
import { Functions, httpsCallable } from "@angular/fire/functions";
import { SwUpdate } from "@angular/service-worker";
import { Subject, debounceTime, firstValueFrom, map, take } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CustomErrorHandler implements ErrorHandler {
  readonly error$ = new Subject<Error>();

  constructor(
    private readonly functions: Functions,
    private readonly auth: Auth,
    private readonly swUpdate: SwUpdate,
  ) {
    this.swUpdate.unrecoverable.subscribe(async e => {
      console.error('Unrecoverable service-worker state:', e);
      await this.reportError(new Error('Unrecoverable service-worker state, forcing reload: ' + e.reason));

      window.location.reload();
    });

    this.error$.pipe(debounceTime(1000), take(20)).subscribe(async error => {
      this.reportError(error);
    });
  }

  async handleError(error: Error) {
    console.error('Uncaught error:', error);

    if(error?.message?.includes('Failed to fetch dynamically imported module') || error?.message?.includes('Importing a module script failed')) {
      window.location.reload();
      return;
    }

    this.error$.next(error);
  }

  private async reportError(error: Error) {
    const url = window.location.toString();
    const uid = await firstValueFrom(user(this.auth).pipe(map(u => u?.uid || '(not logged in)')));
    let message = error?.message || '(no error message)';
    if(message && typeof message !== 'string') {
      try {
        message = JSON.stringify(message);
      } catch (e2) {}
    }

    const stackTrace = error?.stack || '(no stack trace)';
    const userAgent = window.navigator.userAgent || '(unknown user agent)';

    if(!error?.stack && !error?.message) {
      console.warn('No error to report', error);
      return;
    }

    try {
      await httpsCallable<{}, string>(this.functions, 'reportJsError')({url, uid, message, stackTrace, userAgent});
    } catch (e2) {
      console.warn('Failed to report error', e2);
    }
  }
}
